import React from "react";

import useEmblaCarousel from "embla-carousel-react";

import { activateOnEnterOrSpace } from "../utils/keyboardActivation";

const Top10Carousel = ({
  items = [],
  title = "Top 10 This Week",
  onItemClick,
  isDarkMode = true,
  bounded = false,
}) => {
  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: bounded ? "trimSnaps" : false,
    dragFree: true,
    skipSnaps: false,
    watchDrag: true,
    watchSlides: false,
  });

  if (!items || items.length === 0) return null;

  // Take only the top 10 items
  const top10Items = items.slice(0, 10);

  const handleClick = (item) => {
    if (onItemClick) {
      onItemClick(item);
    }
  };

  return (
    <section className="mb-6 overflow-visible">
      {title ? (
        <h2 className="text-xl sm:text-2xl font-semibold text-white pl-4 sm:pl-6 pb-1">
          {title}
        </h2>
      ) : null}
      <div
        className="select-none"
        ref={emblaRef}
        style={
          bounded
            ? {
                padding: "20px 0 10px",
                overflowX: "clip",
                overflowY: "visible",
              }
            : { padding: "20px 16px 10px 16px", overflow: "visible" }
        }
      >
        <div
          className="flex"
          style={{
            overflow: "visible",
            containerType: bounded ? "inline-size" : undefined,
          }}
        >
          {top10Items.map((item, index) => (
            <Top10Item
              bounded={bounded}
              isDarkMode={isDarkMode}
              isLast={index === top10Items.length - 1}
              item={item}
              key={item.mediaId || item.id || index}
              rank={index + 1}
              onClick={() => handleClick(item)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Top10Item = ({
  item,
  rank,
  isLast,
  onClick,
  isDarkMode = true,
  bounded = false,
}) => {
  const posterUrl = item.posterPath || item.poster;

  // Special handling for rank 10 - tighter letter spacing
  const isDoubledigit = rank === 10;
  const rankSpace = isDoubledigit ? 0.92 : 0.6;
  const endSpace = isLast ? 0 : 0.05;
  const sizeClass = bounded
    ? ""
    : `${isLast ? "mr-16 sm:mr-32" : ""} w-[calc((100vw-48px)/2)] sm:w-[calc((100vw-80px)/3)] lg:w-[calc((100vw-116px)/5)] max-[932px]:landscape:w-[calc((100vw-80px)/4)]`;
  const strokeColor = isDarkMode ? "#C0C0C0" : "#94a3b8";

  return (
    <div
      className={`lunova-card-host relative cursor-pointer group overflow-visible flex items-end flex-shrink-0 ${sizeClass}`}
      role="button"
      style={
        bounded
          ? {
              "--p":
                "calc(100cqw * (1 - (var(--grid-cols, 6) - 1) * var(--grid-gap-ratio, 0.03)) / var(--grid-cols, 6))",
              flex: `0 0 calc(var(--p) * ${1 + endSpace + rankSpace})`,
            }
          : { minWidth: "100px" }
      }
      tabIndex={0}
      onClick={onClick}
      onKeyDown={activateOnEnterOrSpace}
    >
      {/* Large Rank Number - Behind poster, responsive font size */}
      <div
        className={`absolute bottom-0 select-none pointer-events-none ${bounded ? "" : "left-0 top10-number"}`}
        style={{
          fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
          fontWeight: 900,
          fontSize: bounded
            ? "calc(var(--p) * 1.125)"
            : "clamp(156px, 29vw, 438px)",
          left: bounded ? "calc(var(--p) * 0.04)" : undefined,
          lineHeight: "0.82",
          letterSpacing: isDoubledigit ? "-0.15em" : "0",
          color: isDarkMode ? "#1A1D2E" : "#c7d2e8",
          WebkitTextStroke: bounded
            ? `calc(var(--p) * 0.03) ${strokeColor}`
            : `3px ${strokeColor}`,
          paintOrder: "stroke fill",
          zIndex: 1,
        }}
      >
        {rank}
      </div>

      {/* Poster Card - Defines container height, pushed right to show numbers */}
      <div
        className="lunova-card card-radius-poster shadow-xl"
        style={{
          width: bounded ? "var(--p)" : "52.5%",
          aspectRatio: "2/3",
          marginLeft: bounded
            ? `calc(var(--p) * ${rankSpace})`
            : isDoubledigit
              ? "calc(38% + 5px)"
              : "calc(32% + 5px)",
          marginRight: bounded ? `calc(var(--p) * ${endSpace})` : undefined,
          zIndex: 2,
          "--card-scale": 1.03,
        }}
      >
        <div className="lunova-card-clip relative">
          {posterUrl ? (
            <img
              alt={item.name || item.title || "Movie poster"}
              className="w-full h-full object-cover"
              draggable={false}
              src={posterUrl}
              onDragStart={(e) => e.preventDefault()}
            />
          ) : (
            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
              <i className="fas fa-film text-4xl text-gray-600" />
            </div>
          )}

          {/* Progress bar overlay */}
          {item.progressPercent > 0 && item.progressPercent < 95 && (
            <>
              <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-2 left-2 right-2 h-1 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500 rounded-full"
                  style={{ width: `${item.progressPercent}%` }}
                />
              </div>
            </>
          )}

          {/* Watched checkmark */}
          {(item.watched || item.progressPercent >= 95) && (
            <div className="absolute bottom-2 right-2 w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center">
              <i className="fas fa-check text-white text-xs" />
            </div>
          )}

          {/* Favorite star */}
          {item.isBookmarked && (
            <div className="absolute bottom-2 right-2">
              <i
                className="fas fa-star text-yellow-400 text-sm"
                style={{
                  textShadow: "0 1px 3px rgba(0,0,0,0.8)",
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Top10Carousel;
